import React, { useCallback, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import {
	Delivery,
	DeliveryStatusEnum,
	DELIVERY_STATUS_LABELS,
} from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "./delivery-status-badge.component";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { uploadFileWithProvidedPath } from "@/dentlab/src/hooks/useStorage";
import { useFileUpload } from "@/dentlab/src/hooks/useFileUpload";
import { sanitizeFileName } from "@/dentlab/src/lib/utils/utils-functions";
import {
	Upload,
	Image as ImageIcon,
	Loader2,
	Check,
	Package,
} from "lucide-react";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import { FileViewerModal } from "@/components/src/file-viewer";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/dentlab/src/components/ui/dropdown-menu";

interface DeliveryFile {
	id: string;
	file_name: string;
	path_name: string;
	bucket_name: string;
	signedUrl?: string;
}

const ALL_STATUSES = [
	DeliveryStatusEnum.PENDING,
	DeliveryStatusEnum.IN_TRANSIT,
	DeliveryStatusEnum.DELIVERED,
	DeliveryStatusEnum.FAILED,
];

const statusDotClasses: Record<DeliveryStatusEnum, string> = {
	[DeliveryStatusEnum.PENDING]: "bg-gray-400",
	[DeliveryStatusEnum.IN_TRANSIT]: "bg-yellow-500",
	[DeliveryStatusEnum.DELIVERED]: "bg-green-500",
	[DeliveryStatusEnum.FAILED]: "bg-red-500",
};

interface DeliveryDetailDialogProps {
	delivery: Delivery | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onStatusChange?: (deliveryId: number, status: DeliveryStatusEnum) => void;
	onUpdate?: (deliveryId: number, fields: Partial<Delivery>) => void;
	onFilesChanged?: () => void;
	courierName?: string;
}

export const DeliveryDetailDialog: React.FC<DeliveryDetailDialogProps> = ({
	delivery,
	open,
	onOpenChange,
	onStatusChange,
	onUpdate,
	onFilesChanged,
	courierName,
}) => {
	const [files, setFiles] = useState<DeliveryFile[]>([]);
	const [filesLoading, setFilesLoading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [labNote, setLabNote] = useState("");
	const [labNoteSaved, setLabNoteSaved] = useState(false);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const lastSavedLabNote = useRef("");
	const [fileViewerOpen, setFileViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	useEffect(() => {
		if (open && delivery) {
			setLabNote(delivery.note ?? "");
			lastSavedLabNote.current = delivery.note ?? "";
			setLabNoteSaved(false);
		}
		if (!open) {
			setFileViewerOpen(false);
			setSelectedFileId(null);
		}
	}, [open, delivery?.id]);

	useEffect(() => {
		return () => {
			if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		};
	}, []);

	const saveLabNote = async (value: string) => {
		if (!delivery || value === lastSavedLabNote.current) return;
		const { error } = await supabase
			.from("deliveries")
			.update({ note: value })
			.eq("id", delivery.id);
		if (error) {
			Logger.error(error, {}, "Error saving lab note");
		} else {
			lastSavedLabNote.current = value;
			setLabNoteSaved(true);
			setTimeout(() => setLabNoteSaved(false), 2000);
		}
	};

	const handleLabNoteChange = (value: string) => {
		setLabNote(value);
		setLabNoteSaved(false);
		if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
		saveTimerRef.current = setTimeout(() => saveLabNote(value), 800);
	};

	const formatDate = (dateStr: string | null) => {
		if (!dateStr) return null;
		return new Date(dateStr).toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const fetchDeliveryFiles = useCallback(async (deliveryId: number) => {
		setFilesLoading(true);
		const { data, error } = await supabase.rpc("get_delivery_files", {
			p_delivery_id: deliveryId,
		});
		if (error) {
			Logger.error(error, {}, "Error fetching delivery files");
			setFilesLoading(false);
			return;
		}
		const fileData = (data || []) as unknown as DeliveryFile[];
		if (fileData.length === 0) {
			setFiles([]);
			setFilesLoading(false);
			return;
		}
		const withUrls = await Promise.all(
			fileData.map(async (f) => {
				const { data: urlData } = await supabase.storage
					.from(f.bucket_name)
					.createSignedUrl(f.path_name, 3600);
				return { ...f, signedUrl: urlData?.signedUrl };
			})
		);
		setFiles(withUrls);
		setFilesLoading(false);
	}, []);

	useEffect(() => {
		if (open && delivery) {
			fetchDeliveryFiles(delivery.id);
		} else {
			setFiles([]);
		}
	}, [open, delivery?.id, fetchDeliveryFiles]);

	const handleFileUpload = async (uploadFiles: File[]) => {
		if (!delivery) return;
		for (const file of uploadFiles) {
			const path = `deliveries/${
				delivery.id
			}/${Date.now()}_${sanitizeFileName(file.name)}`;
			const { success } = await uploadFileWithProvidedPath({
				filePath: path,
				fileBody: file,
				fileOptions: { contentType: file.type },
				bucketName: "v1",
			});
			if (!success) {
				showNotification({
					message: `Fehler beim Hochladen: ${file.name}`,
					type: "error",
				});
				continue;
			}
			const { error } = await supabase.rpc("upload_delivery_file", {
				p_delivery_id: delivery.id,
				p_file_name: file.name,
				p_path_name: path,
				p_bucket_name: "v1",
			});
			if (error) {
				Logger.error(error, {}, "Error registering delivery file");
				showNotification({
					message: `Fehler beim Registrieren: ${file.name}`,
					type: "error",
				});
			}
		}
		posthog.capture("delivery_file_uploaded", {
			file_count: uploadFiles.length,
		});
		showNotification({ message: "Dateien hochgeladen", type: "success" });
		await fetchDeliveryFiles(delivery.id);
		onFilesChanged?.();
	};

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
	});

	const handleOpenFile = (fileId: string) => {
		setSelectedFileId(fileId);
		setFileViewerOpen(true);
	};

	if (!delivery) return null;

	const fileIds =
		files.length > 0 ? files.map((f) => f.id) : delivery.file_ids || [];
	const availableStatuses = ALL_STATUSES.filter((s) => s !== delivery.status);

	return (
		<>
			<Dialog
				open={open}
				onOpenChange={(next) => {
					if (!next && fileViewerOpen) return;
					onOpenChange(next);
				}}
			>
				<DialogContent
					className="max-w-md"
					data-testid="delivery-detail-dialog"
				>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							{onStatusChange ? (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											className="cursor-pointer"
											aria-label="Status ändern"
											data-testid="delivery-detail-status-trigger"
										>
											<DeliveryStatusBadge
												status={delivery.status}
												showChevron
											/>
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="start">
										{availableStatuses.map((s) => (
											<DropdownMenuItem
												key={s}
												className="cursor-pointer"
												onClick={() =>
													onStatusChange(
														delivery.id,
														s
													)
												}
												data-testid={`delivery-detail-status-option-${s}`}
											>
												<span
													className={`h-2 w-2 rounded-full shrink-0 ${statusDotClasses[s]}`}
												/>
												{DELIVERY_STATUS_LABELS[s]}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							) : (
								<DeliveryStatusBadge status={delivery.status} />
							)}
							{courierName && (
								<span className="text-sm font-normal text-gray-500">
									{delivery.courier_type === "person"
										? "Kurier"
										: "Versand"}
									: {courierName}
								</span>
							)}
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-3 pt-1">
						{delivery.description && (
							<div>
								<p className="text-xs font-medium text-gray-500 mb-0.5">
									Beschreibung
								</p>
								<p className="text-sm text-gray-900">
									{delivery.description}
								</p>
							</div>
						)}

						{delivery.scheduled_departure_at && (
							<div>
								<p className="text-xs font-medium text-gray-500 mb-0.5">
									Geplante Abfahrt
								</p>
								<p className="text-sm text-gray-900">
									{formatDate(
										delivery.scheduled_departure_at
									)}
								</p>
							</div>
						)}

						{delivery.delivered_at && (
							<div>
								<p className="text-xs font-medium text-gray-500 mb-0.5">
									Zugestellt
								</p>
								<p className="text-sm text-green-700">
									{formatDate(delivery.delivered_at)}
								</p>
							</div>
						)}

						{onUpdate && (
							<div className="flex items-center gap-2">
								<button
									type="button"
									className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
										delivery.is_final_delivery
											? "border-blue-200 bg-blue-50 text-blue-700"
											: "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
									}`}
									onClick={() =>
										onUpdate(delivery.id, {
											is_final_delivery:
												!delivery.is_final_delivery,
										})
									}
									data-testid="delivery-detail-final-toggle"
									aria-label="Endlieferung umschalten"
								>
									<Package className="h-3 w-3" />
									Endlieferung
								</button>
							</div>
						)}

						{delivery.note && (
							<div>
								<p className="text-xs font-medium text-gray-500 mb-0.5">
									Kurier-Notiz
								</p>
								<p className="text-sm text-gray-900 whitespace-pre-wrap">
									{delivery.note}
								</p>
							</div>
						)}

						<div>
							<div className="flex items-center justify-between mb-1">
								<p className="text-xs font-medium text-gray-500">
									Notiz
								</p>
								{labNoteSaved && (
									<span className="flex items-center gap-1 text-xs text-green-600">
										<Check className="h-3 w-3" />
										Gespeichert
									</span>
								)}
							</div>
							<Textarea
								value={labNote}
								onChange={(e) =>
									handleLabNoteChange(e.target.value)
								}
								rows={2}
								placeholder="Notiz hinterlassen..."
								data-testid="delivery-lab-note-input"
								aria-label="Notiz"
							/>
						</div>

						<div data-testid="delivery-detail-files-section">
							<p className="text-xs font-medium text-gray-500 mb-1.5">
								Fotos / Dateien
							</p>
							{filesLoading && (
								<div className="flex items-center justify-center py-3">
									<Loader2 className="h-4 w-4 animate-spin text-gray-400" />
								</div>
							)}
							{files.length > 0 && (
								<div className="grid grid-cols-3 gap-2 mb-2">
									{files.map((f) => (
										<button
											key={f.id}
											type="button"
											onClick={() => handleOpenFile(f.id)}
											className="block aspect-square rounded-md overflow-hidden border border-gray-100 hover:border-gray-300 transition-colors bg-gray-50 cursor-pointer"
											aria-label={`Datei anzeigen: ${f.file_name}`}
											data-testid={`delivery-file-${f.id}`}
										>
											{f.signedUrl &&
											/\.(jpg|jpeg|png|gif|webp|heic)$/i.test(
												f.file_name
											) ? (
												<img
													src={f.signedUrl}
													alt={f.file_name}
													className="w-full h-full object-cover"
												/>
											) : (
												<div className="w-full h-full flex flex-col items-center justify-center p-1">
													<ImageIcon className="h-6 w-6 text-gray-400 mb-1" />
													<span className="text-[10px] text-gray-500 truncate w-full text-center">
														{f.file_name}
													</span>
												</div>
											)}
										</button>
									))}
								</div>
							)}
							<div
								{...dragHandlers}
								className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${
									isDragging
										? "border-blue-400 bg-blue-50"
										: "border-gray-200 hover:border-gray-300"
								}`}
								onClick={() => fileInputRef.current?.click()}
								data-testid="delivery-detail-upload-zone"
							>
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept="image/*,application/pdf"
									className="hidden"
									onChange={(e) => {
										if (e.target.files?.length) {
											handleFileUpload(
												Array.from(e.target.files)
											);
											e.target.value = "";
										}
									}}
								/>
								{isUploading ? (
									<div className="flex items-center justify-center gap-2">
										<Loader2 className="h-4 w-4 animate-spin text-gray-500" />
										<span className="text-xs text-gray-500">
											Hochladen...
										</span>
									</div>
								) : (
									<div className="flex flex-col items-center gap-1">
										<Upload className="h-4 w-4 text-gray-400" />
										<span className="text-xs text-gray-500">
											Fotos oder Dateien hochladen
										</span>
									</div>
								)}
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			{fileIds.length > 0 && (
				<FileViewerModal
					fileIds={fileIds}
					isOpen={fileViewerOpen}
					onClose={() => {
						setFileViewerOpen(false);
						setSelectedFileId(null);
					}}
					defaultFileId={selectedFileId || undefined}
				/>
			)}
		</>
	);
};
